import { Routes, Route } from "react-router-dom";
import PropTypes from "prop-types";

import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import Wrapper from "components/organisms/Wrapper/Wrapper";
import {
  AccessDenied,
  AdminView,
  Analytics,
  ProviderInvoices,
  CommonSettings,
  Dashboardv2,
  Imports,
  Invoices,
  // Feedback,
  MBLShipmentForm,
  PageNotFound,
  ProviderAnalytics,
  OrderConsolidation,
  QuickQuotes,
  Quotes,
  // QuotesV2,
  RequestForm,
  ReleaseNotes,
  ResetAll,
  ShipmentDashboard,
  ShipmentsV1,
  ShipperShipments,
  ProviderShipments,
  Tasks,
  TrackerForm,
  QuoteApproval,
} from "pages";
import CreateNewShipment from "pages/shipper/RequestForm/components/CreateNewShipment";
import ProviderQuotes from "pages/provider/Quotes/ProviderQuotes";
import QuotationDashboard from "pages/provider/QuotationDetail/QuotationDashboard";
import ImageGallery from "pages/others/MediaGallery";
import DocumentParser from "pages/shipper/DocumentParser/DocumentParser";
import RequestFormV2 from "pages/shipper/RequestFormV2";
import PrivateRoute from "./PrivateRoute";

/**
 * A router component that handles all the routes of the application
 * @returns the routes of the application
 */
function Router({ currentLocale, handleLocaleChange, isStaff, isGuest, setIsLoggedInAs, getPermissions, setIsLoading }) {
  // Get the user type from the permissions
  const { permissions } = usePermissions();
  const { user_type, email, company_shipper_settings } = JSON.parse(permissions || "{}");
  const showMBL = import.meta.env.REACT_APP_ENVIRONMENT_NAME === "qa" || email === "gearbox@cargobase.com";
  const isQuoteRequestFormV2Enabled = company_shipper_settings?.quote_request_form_v2_enabled;
  const isQuickRequestV2FeatureEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_11220_QUICK_REQUEST_V2);
  const isQuickRequestFormV2Enabled = isQuoteRequestFormV2Enabled && isQuickRequestV2FeatureEnabled;

  // If user is staff only return the AdminView route
  if (isStaff)
    return (
      <Routes>
        <Route
          path="*"
          element={
            <PrivateRoute>
              <AdminView setIsLoading={setIsLoading} setIsLoggedInAs={setIsLoggedInAs} getPermissions={getPermissions} />
            </PrivateRoute>
          }
        />
      </Routes>
    );
  // If user is guest only return the /shipments, /shipment/:id/ routes
  if (isGuest)
    return (
      <Routes>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Wrapper hideTopNav tabTitle="Shipments" component={<ShipperShipments />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/shipments"
          element={
            <PrivateRoute>
              <Wrapper hideTopNav tabTitle="Shipments" component={<ShipperShipments />} fillPage />
            </PrivateRoute>
          }
        />
        <Route
          path="shipment/:id/*"
          element={
            <PrivateRoute>
              <ShipmentDashboard />
            </PrivateRoute>
          }
        />
      </Routes>
    );
  // else return the normal routes
  return (
    <Routes>
      {/* Common routes, these are shared by shippers and providers */}
      <Route path="/quotation/quick-approval" element={<QuoteApproval />} />
      <Route path="/image-gallery" element={<ImageGallery />} />
      <Route path="/release-notes" element={<Wrapper hideTopNav tabTitle="Release Notes" component={<ReleaseNotes />} />} />
      <Route path="/resetAll" element={<Wrapper hideTopNav tabTitle="Reset All" component={<ResetAll />} />} />
      <Route
        path="/access-denied"
        element={
          <PrivateRoute>
            <Wrapper hideTopNav tabTitle="Access Denied" component={<AccessDenied />} />
          </PrivateRoute>
        }
      />
      <Route
        path="/page-not-found"
        element={
          <PrivateRoute>
            <Wrapper hideTopNav tabTitle="404 Page Not Found" component={<PageNotFound />} />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/*"
        element={
          <PrivateRoute>
            <CommonSettings currentLocale={currentLocale} handleLocaleChange={handleLocaleChange} />
          </PrivateRoute>
        }
      />
      {/* <Route
        path="/feedback"
        element={
          <PrivateRoute>
            <Wrapper hideTopNav tabTitle="Feedback" component={<Feedback />} />
          </PrivateRoute>
        }
      /> */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Wrapper hideTopNav tabTitle="Dashboard" component={<Dashboardv2 user_type={user_type} />} />
          </PrivateRoute>
        }
      />

      {/* Shipper routes */}
      {user_type === "shipper" && (
        <>
          <Route
            path="shipment/:id/*"
            element={
              <PrivateRoute>
                <ShipmentDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-requests"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Requests" component={<Quotes />} fillPage />
              </PrivateRoute>
            }
          />
          {/* <Route
            path="/quote-requests-v2"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Requests V2" component={<QuotesV2 />} />
              </PrivateRoute>
            }
          /> */}
          <Route
            path="/quick-requests"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quick Requests" component={<QuickQuotes />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Invoices" component={<Invoices />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Analytics" component={<Analytics />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shipments-v1"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="ShipmentsV1" component={<ShipmentsV1 />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/shipments"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Shipments" component={<ShipperShipments />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/quick-request/"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Quick Request"
                  component={
                    isQuickRequestFormV2Enabled ? (
                      <RequestFormV2 isQuickRequest />
                    ) : (
                      <RequestForm isQuoteRequest={false} isQuickRequest />
                    )
                  }
                />
              </PrivateRoute>
            }
          />
          {isQuickRequestV2FeatureEnabled && (
            <Route
              path="/quick-request-v2"
              element={
                <PrivateRoute>
                  <Wrapper hideTopNav tabTitle="Quick Request V2" component={<RequestFormV2 isQuickRequest />} />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/quick-request/:shipmentId/edit"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Edit Quick Request"
                  component={
                    isQuickRequestFormV2Enabled ? (
                      <RequestFormV2 isQuickRequest isEditQuickRequest />
                    ) : (
                      <RequestForm isQuoteRequest={false} isQuickRequest />
                    )
                  }
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-request"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Quote Request"
                  component={isQuoteRequestFormV2Enabled ? <RequestFormV2 /> : <RequestForm isQuoteRequest />}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-request/:shipmentId/edit"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Edit Quote Request"
                  component={
                    isQuoteRequestFormV2Enabled ? (
                      <RequestFormV2 isEditQuoteRequest />
                    ) : (
                      <RequestForm isQuoteRequest isEditQuoteRequest />
                    )
                  }
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-request-v2"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Request V2" component={<RequestFormV2 />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-request-v2/:shipmentId/edit"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Edit Quote Request V2" component={<RequestFormV2 isEditQuoteRequest />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/mini-tender"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Mini Tender" component={<RequestForm isQuoteRequest isMiniTender />} />
              </PrivateRoute>
            }
          />
          <Route
            path="shipment/:shipmentId/edit"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Edit Shipment"
                  component={
                    isQuoteRequestFormV2Enabled ? (
                      <RequestFormV2 isEditShipment />
                    ) : (
                      <RequestForm isQuoteRequest isEditShipment />
                    )
                  }
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/tracker"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Request" component={<TrackerForm />} />
              </PrivateRoute>
            }
          />
          {showMBL && (
            <Route
              path="/MBL-shipment"
              element={
                <PrivateRoute>
                  <Wrapper hideTopNav tabTitle="Quote Request" component={<MBLShipmentForm />} />
                </PrivateRoute>
              }
            />
          )}

          <Route
            path="/create-new-booking"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Request" component={<CreateNewShipment isQuoteRequest />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Tasks" component={<Tasks />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/imports"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Imports" component={<Imports />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/order-consolidation/"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Order Consolidation" component={<OrderConsolidation />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/order-consolidation/:shipmentId/edit"
            element={
              <PrivateRoute>
                <Wrapper
                  hideTopNav
                  tabTitle="Edit Order Consolidation"
                  component={<OrderConsolidation isConsolidateQuoteEditRequest />}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/document-parser"
            element={
              <PrivateRoute>
                <DocumentParser />
              </PrivateRoute>
            }
          />
        </>
      )}
      {/* Provider routes */}
      {user_type === "provider" && (
        <>
          <Route
            path="/invoices"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Invoices" component={<ProviderInvoices />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/quote-requests"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Quote Requests" component={<ProviderQuotes />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/shipments"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Shipments" component={<ProviderShipments />} fillPage />
              </PrivateRoute>
            }
          />
          <Route
            path="shipment/:id/*"
            element={
              <PrivateRoute>
                <QuotationDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="analytics"
            element={
              <PrivateRoute>
                <Wrapper hideTopNav tabTitle="Analytics" component={<ProviderAnalytics />} fillPage />
              </PrivateRoute>
            }
          />
        </>
      )}
      {/* always keep it as a last item so if user is unauthenticated and visit a route
      can be redirected to the login page. */}
      <Route path="*" element={<PrivateRoute />} />
    </Routes>
  );
}

export default Router;

Router.propTypes = {
  currentLocale: PropTypes.string,
  handleLocaleChange: PropTypes.func,
  isStaff: PropTypes.bool,
  isGuest: PropTypes.bool,
  setIsLoggedInAs: PropTypes.func,
  getPermissions: PropTypes.func,
  setIsLoading: PropTypes.func,
};

Router.defaultProps = {
  currentLocale: "",
  handleLocaleChange: () => {},
  isStaff: false,
  isGuest: false,
  setIsLoggedInAs: () => {},
  getPermissions: () => {},
  setIsLoading: () => {},
};
